import { useState, useMemo, useEffect } from 'react'
import { AlertCircle } from 'lucide-react'
import Modal, { FormField, FormInput, FormSelect, FormTextarea, FormActions, Button } from '../shared/Modal'
import { useAccounts } from '../../hooks'

function NewProjectModal({ isOpen, onClose, onCreate, defaultAccount, onManageAccounts, editProject, onUpdate }) {
  const { accounts, loading: accountsLoading, loadAccounts } = useAccounts()
  const [account, setAccount] = useState(defaultAccount || '')
  const [appName, setAppName] = useState('')
  const [description, setDescription] = useState('')
  const [errors, setErrors] = useState({})

  const isEditMode = !!editProject

  // Reload accounts and populate form when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAccounts()
      if (editProject) {
        // Populate form with existing project data
        setAccount(editProject.scoutAccountId || editProject.account || defaultAccount || '')
        setAppName(editProject.name || '')
        setDescription(editProject.description || '')
      } else if (defaultAccount) {
        setAccount(defaultAccount)
      }
    }
  }, [isOpen, defaultAccount, editProject, loadAccounts])

  // Map accounts to select options
  const accountOptions = useMemo(() => {
    return accounts.map(acc => ({
      value: acc.scoutAccountId,
      label: acc.name
    }))
  }, [accounts])

  // Sanitize project name: convert spaces to hyphens, remove invalid chars
  const handleAppNameChange = (value) => {
    // Replace spaces with hyphens, remove any other invalid characters
    const sanitized = value
      .replace(/\s+/g, '-')  // Replace spaces with hyphens
      .replace(/[^a-zA-Z0-9_-]/g, '')  // Remove any other invalid characters
    setAppName(sanitized)
  }

  const validate = () => {
    const newErrors = {}
    if (!account) newErrors.account = 'Account is required'
    if (!appName.trim()) newErrors.appName = 'Project name is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (validate()) {
      const selectedAccount = accounts.find(a => a.scoutAccountId === account)
      const projectData = {
        account,
        accountName: selectedAccount?.name || account,
        name: appName,
        description
      }

      if (isEditMode) {
        // Update existing project
        onUpdate?.({
          ...editProject,
          ...projectData
        })
      } else {
        // Create new project
        onCreate?.(projectData)
      }

      // Reset form
      setAccount('')
      setAppName('')
      setDescription('')
      setErrors({})
    }
  }

  const handleClose = () => {
    setAccount('')
    setAppName('')
    setDescription('')
    setErrors({})
    onClose()
  }

  // Check if no accounts exist
  const hasNoAccounts = !accountsLoading && accounts.length === 0

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? "Edit Project" : "New Project"}
      size="medium"
    >
      {hasNoAccounts ? (
        <div className="empty-state" style={{ padding: '40px 20px', textAlign: 'center' }}>
          <AlertCircle size={48} color="#f59e0b" style={{ marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>No Accounts Available</h3>
          <p style={{ margin: '0 0 20px 0', color: 'var(--text-muted)' }}>
            You need to create at least one account before creating a project.
          </p>
          <FormActions>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button variant="primary" onClick={() => {
              handleClose()
              onManageAccounts?.()
            }}>
              Manage Accounts
            </Button>
          </FormActions>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <FormField label="Account" required error={errors.account}>
            <FormSelect
              value={account}
              onChange={setAccount}
              options={accountOptions}
              placeholder={accountsLoading ? "Loading accounts..." : "Select an account..."}
              disabled={accountsLoading || isEditMode}
            />
            {isEditMode && (
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                Account cannot be changed for existing projects
              </p>
            )}
          </FormField>

        <FormField label="Project Name" required error={errors.appName}>
          <FormInput
            value={appName}
            onChange={handleAppNameChange}
            placeholder="e.g., TestApp01, PayrollSystem"
            disabled={isEditMode}
          />
          {isEditMode ? (
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
              Project name cannot be changed
            </p>
          ) : (
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
              Spaces will be converted to hyphens automatically
            </p>
          )}
        </FormField>

        <FormField label="Description">
          <FormTextarea
            value={description}
            onChange={setDescription}
            placeholder="Optional: Add application notes and description here..."
            rows={4}
          />
        </FormField>

        <p className="form-hint">* Required fields</p>

        <FormActions>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {isEditMode ? "Update Project" : "Create Project"}
          </Button>
        </FormActions>
      </form>
      )}
    </Modal>
  )
}

export default NewProjectModal
